/**
 * React Query bindings for the Team tab (`services/team.service.ts`): the
 * members, their invitations, and the caller's own role. Each write says how it
 * went in a toast; a refusal's toast is the server's sentence.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { isNotFound } from "@/lib/api-client";
import {
  changeMemberRole,
  getTeam,
  type Invitation,
  listInvitations,
  type MemberRole,
  removeMember,
  revokeInvitation,
  sendInvitation,
  type TeamMember,
} from "@/services/team.service";
import { getWorkspace, type WorkspaceRole } from "@/services/workspace.service";

export type WorkspaceMember = TeamMember;
export type WorkspaceInvitation = Invitation;

export interface WorkspaceMembersResult {
  /** Without the owner, whose own membership row the server leaves out. */
  members: WorkspaceMember[];
  ownerEmail: string | null;
}

export const useWorkspaceMembers = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ["workspace-members", workspaceId],
    queryFn: async (): Promise<WorkspaceMembersResult> => {
      const team = await getTeam(workspaceId!);
      return { members: team.members, ownerEmail: team.owner.email };
    },
    enabled: !!workspaceId,
  });
};

export const useWorkspaceInvitations = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ["workspace-invitations", workspaceId],
    queryFn: () => listInvitations(workspaceId!),
    enabled: !!workspaceId,
  });
};

export const useInviteMember = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, email, role }: { workspaceId: string; email: string; role: MemberRole }) =>
      sendInvitation(workspaceId, email, role),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-invitations", variables.workspaceId] });
      toast({
        title: "Invitation sent",
        description: `An invitation has been sent to ${variables.email}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateMemberRole = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, role, workspaceId }: { memberId: string; role: MemberRole; workspaceId: string }) =>
      changeMemberRole(workspaceId, memberId, role),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-members", variables.workspaceId] });
      toast({
        title: "Role updated",
        description: "Member role has been updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update role",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useRemoveMember = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ memberId, workspaceId }: { memberId: string; workspaceId: string }) =>
      removeMember(workspaceId, memberId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-members", variables.workspaceId] });
      toast({
        title: "Member removed",
        description: "Member has been removed from the workspace",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to remove member",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useRevokeInvitation = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invitationId, workspaceId }: { invitationId: string; workspaceId: string }) =>
      revokeInvitation(workspaceId, invitationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-invitations", variables.workspaceId] });
      toast({
        title: "Invitation revoked",
        description: "Invitation has been revoked successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * The user's role in the workspace, as the `workspace` function decides it —
 * the same lookup that guards every write, so a control is shown exactly when
 * its request would be accepted. Null when they have none (the 404 that also
 * answers a deleted workspace).
 */
export const useCurrentUserRole = (workspaceId: string | undefined) => {
  return useQuery({
    queryKey: ["current-user-role", workspaceId],
    queryFn: async (): Promise<WorkspaceRole | null> => {
      try {
        return (await getWorkspace(workspaceId!)).role;
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
    enabled: !!workspaceId,
  });
};
